import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseTransferIssueNotificationBox.css";

function pick(row, ...keys) {
  for (const key of keys) {
    if (row && row[key] !== undefined && row[key] !== null && row[key] !== "") return row[key];
    if (row?.raw && row.raw[key] !== undefined && row.raw[key] !== null && row.raw[key] !== "") return row.raw[key];
  }
  return "";
}

function uniqueRows(rows) {
  const map = new Map();
  rows.forEach((row) => {
    const id = pick(row, "id", "entityId") || pick(row, "voucherNo", "transferNo", "voucher_no");
    if (!map.has(id)) map.set(id, row);
  });
  return Array.from(map.values());
}

function relatedWarehouses(selectedLocation) {
  const code = String(selectedLocation || "").trim().toUpperCase();

  if (["DNGHT", "AYMHT"].includes(code)) return ["DNGHT", "AYMHT"];
  if (["DNGPF", "AYMPF"].includes(code)) return ["DNGPF", "AYMPF"];

  return code ? [code] : ["DNGHT", "AYMHT", "DNGPF", "AYMPF"];
}

function itemText(row) {
  const type = String(pick(row, "discrepancyType", "discrepancy_type", "type", "notificationType")).toUpperCase();

  if (type.includes("EXTRA")) {
    return [
      pick(row, "receivedProductCode", "received_product_code"),
      pick(row, "receivedColor", "received_color"),
      pick(row, "receivedSize", "received_size"),
    ].filter(Boolean).join(" / ");
  }

  if (type.includes("WRONG")) {
    const expected = [
      pick(row, "expectedProductCode", "expected_product_code"),
      pick(row, "expectedColor", "expected_color"),
      pick(row, "expectedSize", "expected_size"),
    ].filter(Boolean).join(" / ");

    const received = [
      pick(row, "receivedProductCode", "received_product_code"),
      pick(row, "receivedColor", "received_color"),
      pick(row, "receivedSize", "received_size"),
    ].filter(Boolean).join(" / ");

    return `${expected || "-"} → ${received || "-"}`;
  }

  return [
    pick(row, "expectedProductCode", "expected_product_code", "receivedProductCode", "received_product_code"),
    pick(row, "expectedColor", "expected_color", "receivedColor", "received_color"),
    pick(row, "expectedSize", "expected_size", "receivedSize", "received_size"),
  ].filter(Boolean).join(" / ");
}

export default function WarehouseTransferIssueNotificationBox({ selectedLocation }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const warehouses = useMemo(() => relatedWarehouses(selectedLocation), [selectedLocation]);
  const warehouseLabel = warehouses.join(", ");

  async function loadRows() {
    try {
      setLoading(true);

      const allRows = [];

      for (const wh of warehouses) {
        const res = await fetch(`${API_BASE}/api/erp/pos/transfer-notifications/${encodeURIComponent(wh)}?limit=100`);
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data.rows) ? data.rows : [];
        allRows.push(...list);
      }

      const filtered = uniqueRows(allRows).filter((row) => {
        const type = String(pick(row, "notificationType", "discrepancyType", "type", "discrepancy_type")).toUpperCase();
        const status = String(pick(row, "status")).toUpperCase();
        const from = String(pick(row, "fromLocationCode", "from_location_code", "fromShop")).toUpperCase();
        const responsible = String(pick(row, "responsibleLocation", "responsible_location")).toUpperCase();

        const isIssue =
          type.includes("EXTRA") ||
          type.includes("WRONG") ||
          type.includes("MISSING") ||
          type.includes("SHORT") ||
          type.includes("LOST");

        const isOpen =
          !status.includes("CLOSED") &&
          !status.includes("COMPLETED") &&
          !status.includes("STOCK_POSTED");

        return isIssue && isOpen && (warehouses.includes(from) || warehouses.includes(responsible));
      });

      setRows(filtered);

      window.__WAREHOUSE_ISSUE_NOTI_DEBUG__ = {
        selectedLocation,
        warehouses,
        count: filtered.length,
        rows: filtered,
      };
    } catch (err) {
      setRows([]);
      window.__WAREHOUSE_ISSUE_NOTI_DEBUG__ = {
        selectedLocation,
        warehouses,
        error: err.message,
      };
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    const timer = setInterval(loadRows, 30000);
    return () => clearInterval(timer);
  }, [warehouseLabel]);

  return (
    <div className="whIssueNotiBox">
      <div className="whIssueNotiBox__head">
        <div>
          <b>Transfer Issue Notifications</b>
          <span>{warehouseLabel} related Extra / Wrong / Missing cases</span>
        </div>
        <button type="button" onClick={loadRows}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="whIssueNotiBox__empty">No transfer issue notifications.</div>
      ) : (
        <div className="whIssueNotiBox__list">
          {rows.map((row, index) => {
            const type = String(pick(row, "discrepancyType", "notificationType", "type", "discrepancy_type")).toUpperCase();
            const transferNo = pick(row, "transferNo", "voucherNo", "transfer_no", "voucher_no");
            const from = pick(row, "fromLocationCode", "from_location_code", "fromShop");
            const to = pick(row, "toLocationCode", "to_location_code", "toShop");
            const reported = pick(row, "reportedByLocation", "reported_by_location");
            const qty = pick(row, "receivedQty", "received_qty", "expectedQty", "expected_qty", "qty");
            const status = pick(row, "status");

            return (
              <div className="whIssueNotiCard" key={pick(row, "id", "entityId") || `${transferNo}-${index}`}>
                <div className="whIssueNotiCard__top">
                  <span className={`whIssueNotiBadge ${type.includes("EXTRA") ? "extra" : type.includes("WRONG") ? "wrong" : "missing"}`}>
                    {type}
                  </span>
                  <b>{transferNo}</b>
                </div>

                <div className="whIssueNotiCard__route">
                  {from} → {to}
                </div>

                <div className="whIssueNotiCard__item">
                  Item: <b>{itemText(row) || "-"}</b> / Qty: <b>{qty || "-"}</b>
                </div>

                <div className="whIssueNotiCard__meta">
                  Reported by {reported || "-"} · Status: {status}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
